import type { FC } from 'react';
import React, { useCallback, useContext } from 'react';
import { styled } from '@compiled/react';

import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { N10, N900, Y50 } from '@atlaskit/theme/colors';

import { ProfileAvatar } from '@confluence/profile';

import { getCommentLink, getUserAccountId } from '../feedCard-helpers';
import type {
	FeedContentQuery_feed_nodes_summaryLineUpdate_FeedEventComment_user as User,
	FeedContentQuery_feed_nodes_summaryLineUpdate_FeedEventComment_comment as CommentField,
} from '../../__types__/FeedContentQuery';
import { RenderedContent } from '../RenderedContent';
import { FeedCardContext } from '../FeedCard/FeedCardContext';
import { useFeedShouldHideComments } from '../useFeedShouldHideComments';

import { CommentInfoLine } from './CommentInfoLine';
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div({
	display: 'flex',
});

const profileWrapperStyles = xcss({
	marginRight: 'space.150',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FullWidth = styled.div({
	flexGrow: 1,
	minWidth: 0,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContentLink = styled.a({
	cursor: 'pointer',
	position: 'absolute',
	width: '100%',
	height: '100%',
	zIndex: 1,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CommentContent = styled.div({
	position: 'relative',
	marginBottom: token('space.050', '4px'),
	borderRadius: '8px',
	background: token('color.background.input', N10),
	color: token('color.text', N900),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CommenterName = styled.div({
	fontWeight: 500,
	marginBottom: token('space.050', '4px'),
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CommentInlineText = styled.div({
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
	background: token('color.background.warning', Y50),
	width: 'fit-content',
	maxWidth: '100%',
	padding: `0px ${token('space.025', '2px')}`,
	marginLeft: token('space.negative.025', '-2px'),
	marginTop: token('space.negative.025', '-2px'),
	marginBottom: token('space.050', '4px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CommenterNameContainer = styled.div({
	display: 'flex',
	alignItems: 'center',
});

type CommentProps = {
	datetime: string;
	user: User;
	comment: CommentField;
	pageId?: string;
	contentType?: string;
	compactComment?: boolean;
};

export const Comment: FC<CommentProps> = ({ datetime, user, comment, pageId, contentType }) => {
	const authorName = user.displayName;
	const url = getCommentLink(comment);
	const authorId = getUserAccountId(user);
	const authorAvatar = user?.profilePicture?.path;
	const inlineText = 'inlineText' in comment.location ? comment.location.inlineText : null;
	const commentId = comment.id;
	const { fireAnalyticsEvent } = useContext(FeedCardContext);

	const onContentLinkClicked = useCallback(() => {
		fireAnalyticsEvent({
			type: 'sendUIEvent',
			action: 'clicked',
			actionSubject: 'link',
			actionSubjectId: 'feedCardViewComment',
			commentId,
			includeNavdex: true,
		});
	}, [fireAnalyticsEvent, commentId]);

	const compactComment = useFeedShouldHideComments();

	return (
		<Wrapper>
			{!compactComment && (
				<Box xcss={profileWrapperStyles}>
					<ProfileAvatar
						userId={authorId}
						src={authorAvatar}
						canViewProfile
						size="small"
						name={authorName ?? ''}
					/>
				</Box>
			)}
			<FullWidth>
				<CommentContent>
					<ContentLink
						href={url}
						onClick={onContentLinkClicked}
						data-testid="comment-content-link"
					/>
					{!compactComment && (
						<CommenterNameContainer>
							<CommenterName>{authorName}</CommenterName>
						</CommenterNameContainer>
					)}
					{inlineText && <CommentInlineText>{inlineText}</CommentInlineText>}
					<RenderedContent content={comment} />
				</CommentContent>
				{!compactComment && (
					<CommentInfoLine
						datetime={datetime}
						comment={comment}
						pageId={pageId}
						contentType={contentType}
					/>
				)}
			</FullWidth>
		</Wrapper>
	);
};
